import { FC } from 'react';
import { UpdateFamilyDto, UserRolesEnum } from '@families-link/shared';
import { getLocalStorageFamilyId } from '../../../_core/utils';
import { useGetFamilyQuery, useUpdateFamilyMutation } from '../../../../store/api/families.api';
import { useCurrentAccessTokenSelector } from '../../../auth';
import { Avatar, Box, Container, IconButton, Typography, SxProps, Theme, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useOpenModalForm } from '../../hooks/modal-forms/use-open-modal-form.hook';
import { getObjectLanguage } from '../../../_core/components/languages-component';
import { AvatarIcon } from '../../../_core/constants/icons.constants';
import { FillEditIcon } from '../../../_core/components/icons';
import { mergeSx } from 'merge-sx';

interface FamilyDetailsProps {
	familyId?: string;
	isEngagementPage?: boolean;
	containerSx?: SxProps<Theme>;
}

export const FamilyDetails: FC<FamilyDetailsProps> = ({
	familyId: propFamilyId,
	containerSx,
	isEngagementPage = false,
}) => {
	const familyId = propFamilyId ?? getLocalStorageFamilyId();

	const currentToken = useCurrentAccessTokenSelector();
	const isRequester = currentToken?.payload.sur === UserRolesEnum.Requester;

	const { data: family, refetch } = useGetFamilyQuery({ id: familyId }, { skip: !familyId });
	const [t] = useTranslation();

	const languageCode = family?.preferedLanguage;
	const optionsLang = Array.isArray(languageCode) ? languageCode.map(getObjectLanguage) : [];
	const options = optionsLang.map((option) => (option ? option.name : '')).join(', ');

	const [updateFamily] = useUpdateFamilyMutation();
	const featchActionEdit = async (data: UpdateFamilyDto) => {
		await updateFamily({ id: data._id.toString(), data }).unwrap();
		refetch();
	};

	const handleOpenModalEdit = useOpenModalForm({
		featchAction: featchActionEdit,
		isCreate: false,
		districtId: family?.districtId,
		submitButtonName: t('families.forms.buttons.save'),
		toastText: 'Family contact was successfully updated',
	});

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				width: '100%',
				gap: 3,
				position: 'relative',
			}}
		>
			{isEngagementPage ? null : (
				<Box sx={{ width: '100%' }}>
					<Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 500 }}>
						{t('families.family-contact')}
					</Typography>

					<Box sx={{ position: 'absolute', right: 0, top: '-5px' }}>
						<Tooltip title={t('buttons.tooltips.edit')}>
							<IconButton
								color="primary"
								disabled={isRequester}
								onClick={() => {
									handleOpenModalEdit(family);
								}}
							>
								<FillEditIcon sx={{ fontSize: 32 }} />
							</IconButton>
						</Tooltip>
					</Box>
				</Box>
			)}
			<Box sx={{ width: '100%', overflowY: 'auto' }}>
				<Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 3 }}>
					{family?.imageUrl ? (
						<Avatar
							src={family?.imageUrl}
							alt={family?.fullName}
							style={{ width: 98, height: 98, borderRadius: '32px' }}
						/>
					) : (
						<Box
							style={{
								width: 98,
								height: 98,
								borderRadius: '32px',
								backgroundColor: 'var(--blue-8-opacity, rgba(36, 57, 106, 0.08))',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
							}}
						>
							<AvatarIcon />
						</Box>
					)}

					<Box sx={{ display: 'flex', gap: 3, width: 'calc(100% - 122px)' }}>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								justifyContent: 'space-between',
								overflow: 'hidden',
								gap: '12px',
								minWidth: '40%',
							}}
						>
							<Box>
								<Typography variant="body2" sx={{ fontWeight: 'regular', color: (theme) => theme.colors.grayBlue }}>
									{t('families.columns.full-name')}
								</Typography>
								<Typography sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>
									{family?.fullName}
								</Typography>
							</Box>
							<Box>
								<Typography variant="body2" sx={{ fontWeight: 'regular', color: (theme) => theme.colors.grayBlue }}>
									{t('families.columns.location')}
								</Typography>
								<Typography sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>
									{family?.location}
								</Typography>
							</Box>
							{!isEngagementPage ? (
								<Box>
									<Typography variant="body2" sx={{ fontWeight: 'regular', color: (theme) => theme.colors.grayBlue }}>
										{t('families.columns.email')}
									</Typography>
									<Typography sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>
										{family?.email ? family?.email : '-'}
									</Typography>
								</Box>
							) : null}
						</Box>

						<Box
							sx={{
								flexGrow: 1,
								display: 'flex',
								flexDirection: 'column',
								height: '100%',
								gap: '12px',
							}}
						>
							{isEngagementPage ? (
								<Box>
									<Typography variant="body2" sx={{ fontWeight: 'regular', color: (theme) => theme.colors.grayBlue }}>
										{t('families.columns.email')}
									</Typography>
									<Typography sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>
										{family?.email ? family?.email : '-'}
									</Typography>
								</Box>
							) : null}

							{!isEngagementPage ? (
								<Box>
									<Typography variant="body2" sx={{ fontWeight: 'regular', color: (theme) => theme.colors.grayBlue }}>
										{t('families.columns.phone-number')}
									</Typography>
									<Typography sx={{ fontWeight: 500 }}>{family?.phoneNumber}</Typography>
								</Box>
							) : null}

							<Box>
								<Typography variant="body2" sx={{ fontWeight: 'regular', color: (theme) => theme.colors.grayBlue }}>
									{t('families.columns.prefered-language')}
								</Typography>
								<Box sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>{options}</Box>
							</Box>
						</Box>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};
